import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import TWEEN from '@tweenjs/tween.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';

import Particles from "./Particles";
import { data } from './data';
import { coloursToArray } from '../../utils';
import './index.css';

import pathToModel from '../../assets/models/christmas.glb';
import pathToTexture from '../../assets/img/textures/gray-circle.png';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.cssRenderer = new CSS2DRenderer();
        this.setupCamera();
        this.setupControls();
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/f8ffe5-06d6a0-1b9aaa-ef476f-ffc43d');//('https://coolors.co/d5573b-885053-777da7-94c9a9-c6ecae');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.domElement.style.position = 'absolute';
        this.domElem.appendChild(this.renderer.domElement);
        this.cssRenderer.setSize(window.innerWidth, window.innerHeight);
        this.cssRenderer.domElement.style.position = 'absolute';
        this.domElem.appendChild(this.cssRenderer.domElement);
        this.disposed = false;
        // this.nClouds = 20;
        this.isFalling = false;
        this.scene.background = this.colourArr[2];
        this.scene.fog = new THREE.Fog(this.colourArr[0], 5, 50);

        // const axis = new THREE.AxesHelper();
        // this.scene.add(axis);

        this.blueSnowBall = [];
        this.yellowSnowBall = [];
        this.snow = [];
        this.setupLight();
        this.loadModels();
        this.setupCSSSubjects();
        //this.makeSky();

        this.setupParticles();
    };

    setupCamera() {
        let VIEW_ANGLE = 70;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 0.1;
        let FAR_PLANE = 100;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(4.823, 2.0978, 3.3769);
        this.camera.lookAt(5.445, 1.519, 1.168);
    };

    setupLight() {
        let pointLight = new THREE.PointLight(this.colourArr[0], 0.2);
        pointLight.position.set(2.5, 2, 2.5);
        this.scene.add(pointLight);

        let pointLight2 = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight2.position.set(-2.5, 2, -2.5);
        this.scene.add(pointLight2);

        let pointLight3 = new THREE.PointLight(this.colourArr[3], 0.8);
        pointLight3.position.set(2.5, 2, -2);
        this.scene.add(pointLight3);

        let hemiLight = new THREE.HemisphereLight(this.colourArr[1], this.colourArr[4], 1);
        hemiLight.position.set(0, 0.5, 0);
        this.scene.add(hemiLight);
    };

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.cssRenderer.domElement);
        this.controls.autoRotate = true;
        this.controls.autoRotateSpeed = 0;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.3;
        this.controls.maxDistance = 20;
        this.controls.minDistance = 1;
    }

    setupParticles() {
        this.particles = new Particles(this.scene, this.colourArr[3], 1000);
    }

    loadModels() {
        const gltfLoader = new GLTFLoader();
        gltfLoader.load(pathToModel, (gltf) => {
            gltf.scene.position.set(0, 0, 0);
            gltf.scene.traverse(child => {
                if (child.isMesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                    if (child.name === 'Mesh_0') { //blue snowball
                        this.blueSnowBall.push(child);
                    }
                    if (child.name === 'Mesh_32') {
                        this.snow.push(child);
                    }
                    getYellowModel(child, this.yellowSnowBall);
                }
            });
            this.scene.add(gltf.scene);
            this.model = gltf.scene;
        });
    }

    setupCSSSubjects() {
        const circleTexture = new THREE.TextureLoader().load(pathToTexture);
        for (let i = 0, l = data.length; i < l; i++) {
            const element = document.createElement('div');
            element.className = 'customSprite';
            element.innerHTML = i;
            element.addEventListener('click', () => {
                const details = document.createElement('div');
                details.className = 'customLabel';
                details.innerHTML = data[i].content;
                element.appendChild(details);
                details.addEventListener('click', () => {
                    element.className = 'hiddenCustomSprite';
                })
            });

            const labelSpriteMaterial = new THREE.SpriteMaterial({
                map: circleTexture,
                depthTest: false,
                depthWrite: false,
                sizeAttenuation: false
            });
            const labelSprite = new THREE.Sprite(labelSpriteMaterial);
            labelSprite.scale.set(0.07, 0.07, 0.07);
            labelSprite.userData.id = i;
            labelSprite.position.copy(data[i].sprite);
            this.scene.add(labelSprite);

            const objectCSS = new CSS2DObject(element);
            objectCSS.position.copy(data[i].description);
            this.scene.add(objectCSS);
        }
    }

    // makeCloud() {
    //     let cloud = new THREE.Object3D();
    //     this.scene.add(cloud);
    //     const geo = new THREE.OctahedronBufferGeometry(1);
    //     const material = new THREE.MeshLambertMaterial({
    //         color: this.colourArr[3],
    //         transparent: true,
    //         opacity: 0.5
    //     })
    //     let nBlocks = 3 + Math.floor(Math.random() * 3);
    //     console.log(nBlocks);
    //     for (let i = 0; i < nBlocks; i++) {
    //         const mesh = new THREE.Mesh(geo, material);
    //         mesh.position.x = i * 0.1;
    //         mesh.position.y = Math.random();
    //         mesh.position.z = Math.random();
    //         mesh.rotation.z = Math.random() * Math.PI * 2;
    //         mesh.rotation.y = Math.random() * Math.PI * 2;
    //         const scale = 0.1 + Math.random() * 0.3;
    //         mesh.scale.set(scale, scale, scale);
    //         cloud.add(mesh);
    //     }
    //     console.log(cloud);
    //     return cloud;
    // }

    // makeSky() {
    //     this.clouds = new THREE.Object3D();
    //     //this.clouds.rotation.x = Math.PI * 2;
    //     this.scene.add(this.clouds);
    //     let step = Math.PI * 2 / this.nClouds;
    //     for (let i = 0; i < this.nClouds; i++) {
    //         let c = this.makeCloud();
    //         let a = step * i; //angle of the cloud
    //         let h = 7 + Math.random() * 2; //distance between the center of the axis and the cloud itself
    //         // c.position.y = step * i * h;
    //         c.position.x = Math.sin(a) * h;
    //         c.position.y = Math.cos(a) * h;
    //         c.rotation.z = a + Math.PI / 2;

    //         //position the clouds at random depths
    //         c.position.z = -4 - Math.random() * 4;
    //         const s = 1 + Math.random() * 2;
    //         c.scale.set(s, s, s);
    //         this.clouds.add(c);
    //     }
    // }

    updateSceneSubjects(time) {
        this.snow.forEach(elem => {
            elem.rotation.z = 0.01 * time;
        });
        if (this.isFalling) this.particles.update(Date.now() * 0.005);
    }

    goToScene() {
        let pos;
        pos = new THREE.Vector3(6.977, 3.035, 7.438);
        new TWEEN.Tween(this.camera.position)
            .to({
                x: pos.x,
                y: pos.y,
                z: pos.z
            }, 1000)
            .easing(TWEEN.Easing.Cubic.Out)
            .start()
    }

    /** target: must have position prop */
    jump(target) {
        const initPos = new THREE.Vector3(target.position);
        new TWEEN.Tween(target.position)
            .to({
                x: target.position.x,
                y: target.position.y + 5,
                z: target.position.z
            }, 200)
            .easing(TWEEN.Easing.Cubic.Out)
            .start()
        new TWEEN.Tween(target.position)
            .to({
                x: target.position.x,
                y: target.position.y + 5,
                z: target.position.z
            }, 200)
            .easing(TWEEN.Easing.Cubic.Out)
            .start()
            .onComplete(() => {
                new TWEEN.Tween(target.position)
                    .to({
                        x: initPos.x,
                        y: initPos.y,
                        z: initPos.z
                    }, 200)
                    .easing(TWEEN.Easing.Bounce.Out)
                    .start()
            })
    }

    scatter(target) {
        const initPos = new THREE.Vector3(target.position);
        new TWEEN.Tween(target.position)
            .to({
                x: target.position.x + 2,
                y: target.position.y,
                z: target.position.z + 3
            }, 200)
            .easing(TWEEN.Easing.Cubic.Out)
            .start()
        new TWEEN.Tween(target.position)
            .to({
                x: target.position.x,
                y: target.position.y + 5,
                z: target.position.z
            }, 200)
            .easing(TWEEN.Easing.Cubic.Out)
            .start()
            .onComplete(() => {
                new TWEEN.Tween(target.position)
                    .to({
                        x: initPos.x,
                        y: initPos.y,
                        z: initPos.z
                    }, 200)
                    .easing(TWEEN.Easing.Cubic.Out)
                    .start()
            })
    }

    update() {
        if (this.disposed) return;
        if (this.controls) this.controls.update();
        if (TWEEN) TWEEN.update();
        this.updateSceneSubjects(Date.now() * 0.005);
        this.renderer.render(this.scene, this.camera);
        this.cssRenderer.render(this.scene, this.camera);
    };

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.cssRenderer.setSize(window.innerWidth, window.innerHeight);
    };

    onKeyDown(e) {
        /** dirty hard-coded, keep track manually of the array bounds */
        switch (e.keyCode) {
            case 67: // C
                this.jump(this.yellowSnowBall[11]);
                break;
            case 76: // L
                this.jump(this.blueSnowBall[0]);
                this.jump(this.yellowSnowBall[1]);
                break;
            case 79: // O
                this.jump(this.yellowSnowBall[1]);
                break;
            case 85: // U
                this.jump(this.yellowSnowBall[2]);
                this.jump(this.blueSnowBall[0]);
                break;
            case 68: // D
                this.jump(this.yellowSnowBall[3]);
                this.jump(this.yellowSnowBall[1]);
                break;
            case 83: // S
                this.jump(this.yellowSnowBall[4]);
                this.jump(this.yellowSnowBall[13]);
                break;
            case 70: // F
                this.jump(this.yellowSnowBall[5]);
                this.jump(this.yellowSnowBall[1]);
                break;
            case 82: // R
                this.jump(this.yellowSnowBall[6]);
                this.jump(this.yellowSnowBall[12]);
                this.isFalling = true;
                this.controls.autoRotateSpeed = 0.7;
                break;
            case 73: // I
                this.jump(this.yellowSnowBall[7]);
                this.jump(this.yellowSnowBall[13]);
                this.jump(this.blueSnowBall[0]);
                this.goToScene();
                break;
            case 84: // T
                this.jump(this.yellowSnowBall[8]);
                break;
            case 65: // A
                this.jump(this.yellowSnowBall[9]);
                this.jump(this.yellowSnowBall[1]);
                break;
            case 32: // Space
                this.jump(this.blueSnowBall[0]);
                this.yellowSnowBall.forEach(element => {
                    this.scatter(element);
                });
                this.snow.forEach(element => {
                    this.scatter(element);
                });
                break;
            case 89: // Y
                this.jump(this.yellowSnowBall[9]);
                this.jump(this.yellowSnowBall[1]);
                break;
            default:
        }
    };

    dispose() {
        this.disposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
        this.domElem.removeChild(this.renderer.domElement);
    };
}

/** hard coded helper to pull the meshes from the yellow model */
const getYellowModel = (child, model) => {
    for (let i = 1; i < 15; i++) {
        if (child.name === `Mesh_${i}`) { //yellow snowball Mesh_1-14
            model.push(child);
        }
    }
}



